// TanStack Query hooks for the owner pass builder (slice 4a-3; multi-program
// since SLICE-PASS-TYPES).
//
// A tenant can run multiple loyalty programs, so the per-program query is keyed
// by id (`programKey(id)`). `usePrograms()` returns the list for the program
// landing page; `useProgram(id)` resolves a single program (the builder) and
// resolves to `null` on the expected "no program" 404.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { ProgramDraftPatch } from '@naya/shared'

import { OwnerApiError, ownerApi } from '../../ownerApi.js'
import type {
  PassType,
  Program,
  ProgramResponse,
  ProgramsListResponse,
  StampIconUploadResponse,
} from '../types.js'

export const programsListKey = ['owner', 'programs'] as const
export const programKey = (id: string) => ['owner', 'program', id] as const

/** All of the tenant's programs + the loyalty cap/usage (the program
 *  landing/list page + the enroll picker). */
export function usePrograms() {
  return useQuery<ProgramsListResponse>({
    queryKey: programsListKey,
    // Always revalidate on mount so status / is_default reflect reality every
    // time the owner lands on the list (a program may have been activated or
    // made default in another tab/flow).
    refetchOnMount: 'always',
    queryFn: () => ownerApi.get<ProgramsListResponse>('/owner/programs'),
  })
}

/**
 * A single program by id, or `null` if it doesn't exist (404). The builder
 * uses this — and derives `cosmeticOnly` (draft vs active) from `status`, so it
 * MUST reflect the program's current status, not a stale cache. `staleTime: 0`
 * + `refetchOnMount: 'always'` revalidate on every builder entry; mutations
 * still update the cache directly via setQueryData for instant feedback.
 */
export function useProgram(id: string) {
  return useQuery<Program | null>({
    queryKey: programKey(id),
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      try {
        const res = await ownerApi.get<ProgramResponse>(`/owner/program/${id}`)
        return res.program
      } catch (err) {
        if (
          err instanceof OwnerApiError &&
          err.status === 404 &&
          err.code === 'PROGRAM_NOT_FOUND'
        ) {
          return null
        }
        throw err
      }
    },
  })
}

/**
 * Create a draft program. Defaults to a loyalty card; pass `'membership'` to
 * mint a membership draft (POST body `{ pass_type }`). The server defaults to
 * loyalty when the body is omitted, so the loyalty call sends no body.
 */
export function useCreateProgram() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (passType: PassType = 'loyalty') =>
      ownerApi.post<ProgramResponse>(
        '/owner/program',
        passType === 'loyalty' ? undefined : { pass_type: passType },
      ),
    onSuccess: (res) => {
      qc.setQueryData(programKey(res.program.id), res.program)
      void qc.invalidateQueries({ queryKey: programsListKey })
    },
  })
}

export function usePatchProgram(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: ProgramDraftPatch) =>
      ownerApi.patch<ProgramResponse>(`/owner/program/${id}`, patch),
    onSuccess: (res) => {
      qc.setQueryData(programKey(id), res.program)
      void qc.invalidateQueries({ queryKey: programsListKey })
    },
  })
}

export function useActivateProgram(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      ownerApi.post<ProgramResponse>(`/owner/program/${id}/activate`),
    onSuccess: (res) => {
      qc.setQueryData(programKey(id), res.program)
      void qc.invalidateQueries({ queryKey: programsListKey })
    },
  })
}

/** Set a program as the tenant's default for the enroll surfaces. */
export function useMakeDefaultProgram() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      ownerApi.post(`/owner/programs/${id}/make-default`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: programsListKey })
    },
  })
}

/** Toggle whether staff may enroll customers into a program (program list). */
export function useSetStaffEnrollable() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      ownerApi.patch<ProgramResponse>(`/owner/program/${id}`, {
        staff_enrollable: value,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: programsListKey })
    },
  })
}

/** Toggle whether staff may recover passes for a program's cards (program
 *  list). Drives the "Recover pass" button in the staff customer search. */
export function useSetStaffCanRecoverPass() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      ownerApi.patch<ProgramResponse>(`/owner/program/${id}`, {
        staff_can_recover_pass: value,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: programsListKey })
    },
  })
}

/**
 * Upload a custom stamp-icon pair (filled + empty). Each is a 200×200 PNG Blob
 * from the crop modal. Returns the two stored URLs — the caller PATCHes them
 * into pass_design.
 */
export function useUploadStampIcon() {
  return useMutation({
    mutationFn: (pair: { filled: Blob; empty: Blob }) => {
      const form = new FormData()
      form.append('filled', pair.filled, 'filled.png')
      form.append('empty', pair.empty, 'empty.png')
      return ownerApi.upload<StampIconUploadResponse>(
        '/owner/program/stamp-icon',
        form,
      )
    },
  })
}

/**
 * Upload the cropped + brightness-tuned hero photo. The Blob is the 1032×336
 * PNG produced by the HeroUploadModal. The server stores the photo, derives
 * the brightened wallet copy, and PATCHes pass_design. Mutation returns the
 * full updated program so the query cache stays consistent.
 */
export function useUploadHero(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { blob: Blob; brightness: number }) => {
      const form = new FormData()
      form.append('hero', input.blob, 'hero.png')
      form.append('brightness', String(input.brightness))
      return ownerApi.upload<ProgramResponse>(
        `/owner/program/${id}/hero`,
        form,
      )
    },
    onSuccess: (res) => {
      qc.setQueryData(programKey(id), res.program)
      void qc.invalidateQueries({ queryKey: programsListKey })
    },
  })
}

/**
 * Re-edit ONLY the brightness on the existing hero photo. The server pulls
 * the cropped original from R2 server-side (no CORS), re-applies brightness,
 * uploads a fresh GCS derived, and patches pass_design. Used by the "Adjust
 * brightness" button — avoids the browser-side R2 fetch that CORS blocks.
 */
export function useUpdateHeroBrightness(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (brightness: number) =>
      ownerApi.patch<ProgramResponse>(`/owner/program/${id}/hero/brightness`, {
        brightness,
      }),
    onSuccess: (res) => {
      qc.setQueryData(programKey(id), res.program)
    },
  })
}

/**
 * Remove the owner's hero photo. Resets pass_design.hero_* to defaults.
 * Idempotent server-side.
 */
export function useDeleteHero(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => ownerApi.del<ProgramResponse>(`/owner/program/${id}/hero`),
    onSuccess: (res) => {
      qc.setQueryData(programKey(id), res.program)
    },
  })
}
